import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { supabase } from '@/integrations/supabase/client'

export const LoginPage = () => {
    const { signIn, user } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()
    const [formData, setFormData] = useState({ email: '', password: '' })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const from = location.state?.from?.pathname || '/dashboard'

    if (user) {
        return <Navigate to={from} replace />
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formData.email || !formData.password) {
            setError('Veuillez remplir tous les champs')
            return
        }

        try {
            setLoading(true)
            setError('')
            const { error } = await signIn(formData.email, formData.password)
            if (error?.code === 'email_not_confirmed') {
                // Send a fresh confirmation code and let the user type it. If Supabase
                // refuses the resend (60s rule), the last code sent is still valid.
                await supabase.auth.resend({ type: 'signup', email: formData.email })
                navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`)
                return
            }
            if (error) throw error
        } catch (error: any) {
            setError(error.message || 'Échec de la connexion')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="flex justify-center">
                        <img src="/logo_doucheflux.jpeg" alt="DoucheFLUX Housing First" className="h-40 object-contain" />
                    </div>
                    <h4 className="mt-6 text-center text-lg font-light text-gray-600">
                        Se connecter à</h4>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Ou{' '}
                        <Link to="/register" className="font-medium text-primary hover:text-primary/80">
                            créer un nouveau compte
                        </Link>
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="rounded-md bg-red-50 p-4">
                            <div className="text-sm text-red-700">{error}</div>
                        </div>
                    )}
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="email" className="sr-only">
                                Adresse e-mail
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                                placeholder="Adresse e-mail"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">
                                Mot de passe
                            </label>
                            <PasswordInput
                                id="password"
                                name="password"
                                autoComplete="current-password"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                                placeholder="Mot de passe"
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <Link
                            to="/reset-password-request"
                            className="text-sm text-primary hover:text-primary/80"
                        >
                            Mot de passe oublié ?
                        </Link>
                    </div>

                    <div>
                        <Button
                            type="submit"
                            className="group relative w-full flex justify-center"
                            disabled={loading}
                        >
                            {loading ? 'Connexion...' : 'Se connecter'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}